import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { LevelBadge, LevelProgress } from "@/components/LevelBadge";
import TermsModal from "@/components/TermsModal";
import { Trophy, Lock, Timer, ArrowRight, Zap, Crown, Medal, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "@/hooks/usePageMeta";

const POSITION_COLOR = {
  QB: "#00FF66",
  WR: "#007AFF",
  RB: "#FF9F0A",
  TE: "#BF5AF2",
  DEF: "#FF3B30",
};

const formatCap = (n) => {
  if (!n) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(0)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
};

export default function Lobby() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [contests, setContests] = useState([]);
  const [stats, setStats] = useState(null);
  const [posFilter, setPosFilter] = useState("ALL");
  const [modeFilter, setModeFilter] = useState("sandbox");
  const [loading, setLoading] = useState(true);
  const [geoInfo, setGeoInfo] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [myContestsMap, setMyContestsMap] = useState({});

  usePageMeta({
    title: "Contest Lobby · PickemSports.club",
    description: "Live daily fantasy football contests. Enter Top 5 QB, RB, WR, TE, or Defense contests across your tier — Rookie, Gamer, Pro, and Elite.",
    path: "/lobby",
    noindex: true,
  });

  const load = async () => {
    try {
      const [{ data: c }, { data: s }, { data: g }, { data: m }] = await Promise.all([
        api.get(`/contests?mode=${modeFilter}`),
        api.get("/users/me/stats"),
        api.get("/geo/check"),
        api.get("/users/me/contests-map"),
      ]);
      setContests(c);
      setStats(s);
      setGeoInfo(g);
      setMyContestsMap(m);
    } catch (e) {
      toast.error("Failed to load lobby");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [modeFilter]);

  const filteredContests = posFilter === "ALL" ? contests : contests.filter((c) => c.position === posFilter);
  const levelOrder = ["rookie", "gamer", "pro", "elite"];
  const userLevelIdx = levelOrder.indexOf(user?.level || "rookie");

  return (
    <Layout>
      {/* Geo / Terms status banner */}
      {(geoInfo || (user && (!user.terms_accepted_at || !user.date_of_birth))) && (
        <div className="mb-4 border border-white/10 bg-[#141414] p-3 md:p-4 flex items-center gap-x-3 gap-y-2 flex-wrap" data-testid="lobby-status-banner">
          {geoInfo && (
            <div className="flex items-center gap-2 min-w-0 basis-full sm:basis-auto sm:flex-1">
              <MapPin
                size={16}
                style={{
                  color:
                    geoInfo.classification?.status === "legal" ? "#00FF66"
                    : geoInfo.classification?.status === "blocked" ? "#FF3B30"
                    : geoInfo.classification?.status === "restricted" ? "#FFB300"
                    : "#71717A",
                }}
              />
              <span className="text-white/60 text-sm truncate">
                {geoInfo.state_name ? `Playing from ${geoInfo.state_name}` : "Location unknown"}
              </span>
              <span
                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ml-1"
                style={{
                  color:
                    geoInfo.classification?.status === "legal" ? "#00FF66"
                    : geoInfo.classification?.status === "blocked" ? "#FF3B30"
                    : geoInfo.classification?.status === "restricted" ? "#FFB300"
                    : "#71717A",
                  borderColor:
                    (geoInfo.classification?.status === "legal" ? "#00FF66"
                    : geoInfo.classification?.status === "blocked" ? "#FF3B30"
                    : geoInfo.classification?.status === "restricted" ? "#FFB300"
                    : "#71717A") + "66",
                }}
              >
                {geoInfo.classification?.status}
              </span>
            </div>
          )}
          {user?.terms_accepted_at && user?.date_of_birth ? (
            <span className="inline-flex items-center gap-1.5 text-[#00FF66] text-xs uppercase tracking-widest">
              <ShieldCheck size={14} /> Age &amp; terms verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-white/40 text-xs uppercase tracking-widest">
              <ShieldCheck size={14} /> Free play · no entry fees, no cash prizes
            </span>
          )}
        </div>
      )}

      {stats && (
        <div className="mb-8 border border-white/10 bg-[#141414] p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <div className="text-white/40 text-xs uppercase tracking-widest">Welcome back</div>
              <div className="font-display text-3xl">{user?.name?.toUpperCase()}</div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Contests</div>
                <div className="font-display text-2xl">{user?.total_contests || 0}</div>
              </div>
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Wins</div>
                <div className="font-display text-2xl text-[#00FF66]">{user?.total_wins || 0}</div>
              </div>
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Wallet · frozen</div>
                <div className="font-display text-2xl text-white/40">${(user?.wallet_balance || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
          <LevelProgress
            level={user?.level || "rookie"}
            xp={user?.xp || 0}
            levelInfo={stats.level_info}
            testId="lobby-level-progress"
          />
        </div>
      )}

      {/* Mode tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-6">
        <button
          onClick={() => setModeFilter("sandbox")}
          data-testid="mode-sandbox"
          className={`px-4 md:px-5 py-3 text-xs md:text-sm uppercase tracking-widest font-semibold transition-colors border-b-2 flex items-center gap-2 ${
            modeFilter === "sandbox" ? "border-[#00FF66] text-white" : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          <Zap size={14} /> Free Play
        </button>
        <button
          onClick={() => setModeFilter("real")}
          data-testid="mode-real"
          className={`px-4 md:px-5 py-3 text-xs md:text-sm uppercase tracking-widest font-semibold transition-colors border-b-2 flex items-center gap-2 ${
            modeFilter === "real" ? "border-[#FF9F0A] text-white" : "border-transparent text-white/40 hover:text-white/70"
          }`}
        >
          Game Day
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-[#FF9F0A]/40 text-[#FF9F0A] tracking-widest">
            Soon
          </span>
        </button>
      </div>

      {modeFilter === "real" ? (
        <div className="border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.05] p-10 text-center"
          data-testid="game-day-coming-soon">
          <div className="text-[#FF9F0A] text-xs uppercase tracking-widest mb-2">Game Day</div>
          <h1 className="font-display text-4xl md:text-5xl mb-3">COMING SOON</h1>
          <p className="text-white/60 text-sm max-w-xl mx-auto">
            Pay-as-you-play contests are on hold. Right now every challenge on PickemSports is a
            free skills contest — we collect no entry fees and pay no cash prizes.
          </p>
          <button
            onClick={() => setModeFilter("sandbox")}
            data-testid="goto-free-play-btn"
            className="mt-5 bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-5 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
          >
            Play a free challenge <ArrowRight size={14} />
          </button>
        </div>
      ) : (
      <>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-1">
            Free skills challenges · no entry fee, no cash prizes
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">CONTEST LOBBY</h1>
        </div>
        <div className="-mx-4 md:mx-0 px-4 md:px-0 w-full md:w-auto overflow-x-auto no-scrollbar">
          <div className="flex gap-1 w-max border border-white/10 rounded-sm p-1 bg-[#141414]">
            {["ALL", "QB", "WR", "RB", "TE", "DEF"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setPosFilter(f)}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm transition-colors ${
                  posFilter === f ? "bg-white text-black" : "text-white/50 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-center py-20 font-display tracking-widest text-2xl animate-pulse">
          LOADING CONTESTS...
        </div>
      ) : filteredContests.length === 0 ? (
        <div className="border border-white/10 bg-[#141414] p-12 text-center">
          <div className="font-display text-2xl mb-2">NO CONTESTS AVAILABLE</div>
          <p className="text-white/50 text-sm">Fresh free practice contests are seeded automatically every 30s.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="contest-grid">
          {filteredContests.map((c) => {
            const posColor = POSITION_COLOR[c.position] || "#00FF66";
            const locked = levelOrder.indexOf(c.tier_level) > userLevelIdx;
            const isSandbox = c.mode === "sandbox";
            const locksSoon = new Date(c.lock_time) - new Date() < 60 * 60 * 1000;
            return (
              <div
                key={c.contest_id}
                data-testid={`contest-card-${c.contest_id}`}
                className={`group border border-white/10 bg-[#141414] p-5 transition-all hover:-translate-y-1 hover:border-white/30 ${
                  locked ? "opacity-60" : "cursor-pointer"
                } ${isSandbox ? "ring-1 ring-[#FF9F0A]/30" : ""}`}
                onClick={() => !locked && navigate(`/contest/${c.contest_id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div
                      className="text-xs uppercase tracking-widest font-bold mb-1"
                      style={{ color: posColor }}
                    >
                      {c.position} ·{" "}
                      {c.title.replace("⚡ Sandbox · ", "").replace("Practice · ", "").replace(/^Top 5 /, "")}
                    </div>
                    <div className="font-display text-2xl tracking-wide">TOP 5 {c.position}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <LevelBadge level={c.tier_level} size="sm" />
                    {isSandbox && (
                      <span className="text-[10px] uppercase tracking-widest text-[#FF9F0A] border border-[#FF9F0A]/40 px-2 py-0.5 rounded-sm">
                        PRACTICE · FREE
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 border-t border-white/10 pt-4 mb-4">
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-widest">Entry</div>
                    <div className="font-display text-xl">FREE</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-widest">Prize</div>
                    <div className="font-display text-xl text-white/40">NONE</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-widest">Field</div>
                    <div className="font-display text-xl">
                      {c.current_entries}
                      <span className="text-white/40 text-sm">
                        /{c.max_entries ? formatCap(c.max_entries) : "—"}
                      </span>
                    </div>
                    <div className="h-1 bg-white/5 mt-1 rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-[#00FF66]"
                        style={{
                          width: `${Math.min(100, (c.current_entries / (c.max_entries || 1)) * 100)}%`,
                          transition: "width 300ms ease",
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-white/40 uppercase tracking-widest mb-3">
                  <span className={`inline-flex items-center gap-1.5 ${locksSoon ? "text-[#FF9F0A]" : ""}`}>
                    <Timer size={12} /> Locks{" "}
                    {new Date(c.lock_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Trophy size={12} /> Bragging rights
                  </span>
                </div>

                {/* Free challenges award no cash — bragging rights only */}
                <div
                  className="mb-4 border border-[#00FF66]/30 bg-[#00FF66]/5 text-[#00FF66] text-[11px] uppercase tracking-widest text-center py-2"
                  data-testid={`practice-note-${c.contest_id}`}
                >
                  Free entry · bragging rights, XP &amp; leaderboard · no cash prize
                </div>

                {locked ? (
                  <div className="w-full py-2.5 text-center bg-white/5 text-white/40 uppercase tracking-widest text-xs font-bold inline-flex items-center justify-center gap-2">
                    <Lock size={12} /> Reach {c.tier_level.toUpperCase()} to unlock
                  </div>
                ) : myContestsMap[c.contest_id] > 0 ? (
                  <div
                    data-testid={`field-position-${c.contest_id}`}
                    className="w-full py-2.5 text-center border border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66] uppercase tracking-widest text-xs font-bold inline-flex items-center justify-center gap-2"
                  >
                    You + {Math.max(0, c.current_entries - 1).toLocaleString()} in this challenge
                  </div>
                ) : (
                  <div className="w-full py-2.5 text-center bg-white/5 group-hover:bg-[#00FF66] group-hover:text-black text-white uppercase tracking-widest text-xs font-bold inline-flex items-center justify-center gap-2 transition-colors">
                    Enter Free Challenge <ArrowRight size={14} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
    </Layout>
  );
}
